import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { formatINR, formatCarats } from '@/lib/format';
import { ChevronRight, ShoppingCart, Hand, Hammer, ArrowRight, AlertTriangle } from 'lucide-react';

/**
 * ConvertToStockDialog — Phase 11
 *
 * Multi-step conversion dialog for an on-approval packet:
 *   1. Choose Scope (Full / Partial)
 *   2. If Partial: pick how much in-hand to convert
 *   3. If Partial: decide what to do with the remainder (keep / return)
 *   4. Choose Target Packet (create new / add to existing same-classification packet)
 *   5. Review & Confirm
 *
 * Goldsmith-issued material on the source packet is **always** auto-converted —
 * the user cannot leave it on approval (per Phase 11 spec).
 */
export function ConvertToStockDialog({ open, onOpenChange, packetId, onConverted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // form state
  const [mode, setMode] = useState('full');
  const [convCt, setConvCt] = useState('');
  const [convPcs, setConvPcs] = useState('');
  const [remainderAction, setRemainderAction] = useState('keep');
  const [targetMode, setTargetMode] = useState('new');
  const [targetId, setTargetId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [note, setNote] = useState('');
  // Wave 3.36 — Capture credit days for the new payable created on Convert to Stock.
  const [creditDays, setCreditDays] = useState('');

  const reset = () => {
    setMode('full'); setConvCt(''); setConvPcs('');
    setRemainderAction('keep');
    setTargetMode('new'); setTargetId(''); setNewDescription(''); setNote('');
    setCreditDays('');
  };

  useEffect(() => {
    if (!open || !packetId) { setPreview(null); return; }
    reset();
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/diamonds/packets/${packetId}/convert-preview`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Preview failed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, packetId]);

  if (!open) return null;

  const inHand = preview?.in_hand || { carats: 0, pieces: 0, value: 0 };
  const gsHeld = preview?.goldsmith_held || { carats: 0, pieces: 0, value: 0 };
  const totalOut = preview?.total_outstanding || { carats: 0, pieces: 0, value: 0 };
  const rate = Number(preview?.rate_per_ct || 0);
  const targets = preview?.eligible_target_packets || [];

  // Computed values for review
  const willConvertCt = mode === 'full'
    ? Number(inHand.carats) + Number(gsHeld.carats)
    : (Number(convCt) || 0) + Number(gsHeld.carats);
  const willConvertPcs = mode === 'full'
    ? Number(inHand.pieces) + Number(gsHeld.pieces)
    : (Number(convPcs) || 0) + Number(gsHeld.pieces);
  const willConvertValue = willConvertCt * rate;
  const remainCt = mode === 'partial'
    ? Math.max(0, Number(inHand.carats) - (Number(convCt) || 0))
    : 0;
  const remainPcs = mode === 'partial'
    ? Math.max(0, Number(inHand.pieces) - (Number(convPcs) || 0))
    : 0;
  const remainValue = remainCt * rate;

  const validatePartial = () => {
    if (mode !== 'partial') return null;
    const ct = Number(convCt) || 0;
    const pcs = Number(convPcs) || 0;
    if (ct <= 0 && pcs <= 0) return 'Enter carats or pieces to convert';
    if (ct - Number(inHand.carats) > 1e-9) return `Cannot convert ${ct} ct (only ${inHand.carats} ct in hand)`;
    if (pcs > Number(inHand.pieces)) return `Cannot convert ${pcs} pcs (only ${inHand.pieces} pcs in hand)`;
    return null;
  };
  const validateTarget = () => {
    if (targetMode === 'existing' && !targetId) return 'Select an existing target packet';
    return null;
  };
  const error = validatePartial() || validateTarget();

  const submit = async () => {
    if (error) { toast.error(error); return; }
    setBusy(true);
    try {
      const payload = {
        mode,
        remainder_action: remainderAction,
        target_packet_mode: targetMode,
        target_packet_id: targetMode === 'existing' ? targetId : null,
        new_packet_description: newDescription,
        note,
      };
      if (mode === 'partial') {
        payload.convert_carats = Number(convCt) || 0;
        payload.convert_pieces = Number(convPcs) || 0;
      }
      // Wave 3.36 — Pass credit_days through when provided.
      if (creditDays !== '') {
        const n = parseInt(creditDays, 10);
        if (!Number.isNaN(n) && n > 0) payload.credit_days = n;
      }
      const { data } = await api.post(`/diamonds/packets/${packetId}/convert-to-stock`, payload);
      toast.success(
        `Converted ${data.converted_carats} ct / ${data.converted_pieces} pcs → ${data.target_packet.packet_no}`
        + (data.vendor_return_memo_no ? ` · Return memo ${data.vendor_return_memo_no}` : '')
      );
      onConverted && onConverted(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Conversion failed')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="convert-to-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart size={16} /> Convert On-Approval Packet to Stock
          </DialogTitle>
          <DialogDescription>
            Move stock from the on-approval bucket into a purchased packet. Goldsmith-issued material is automatically treated as purchased.
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="text-slate-500 py-8 text-center">Loading packet state…</div>
        ) : (
          <div className="space-y-4">
            {/* Breakdown row */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-3 bg-emerald-50 border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 flex items-center gap-1"><Hand size={11} /> In Hand</div>
                <div className="font-mono text-sm text-emerald-900">{formatCarats(inHand.carats)} ct · {inHand.pieces} pcs</div>
                <div className="font-mono text-[11px] text-emerald-700">{formatINR(inHand.value)}</div>
              </Card>
              <Card className="p-3 bg-amber-50 border-amber-200">
                <div className="text-[10px] uppercase tracking-wide text-amber-700 flex items-center gap-1"><Hammer size={11} /> With Goldsmith</div>
                <div className="font-mono text-sm text-amber-900">{formatCarats(gsHeld.carats)} ct · {gsHeld.pieces} pcs</div>
                <div className="font-mono text-[11px] text-amber-700">{formatINR(gsHeld.value)}</div>
              </Card>
              <Card className="p-3 bg-slate-50 border-slate-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-600">Total Outstanding</div>
                <div className="font-mono text-sm text-slate-900">{formatCarats(totalOut.carats)} ct · {totalOut.pieces} pcs</div>
                <div className="font-mono text-[11px] text-slate-700">{formatINR(totalOut.value)} @ ₹{rate.toLocaleString('en-IN')}/ct</div>
              </Card>
            </div>

            {gsHeld.carats > 0 && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span>
                  Goldsmith-held material ({formatCarats(gsHeld.carats)} ct · {gsHeld.pieces} pcs · {formatINR(gsHeld.value)}) is
                  always converted automatically and re-attributed to the target packet.
                </span>
              </div>
            )}

            {/* Scope */}
            <div>
              <Label>Conversion Scope</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-2 gap-2 mt-1">
                <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${mode === 'full' ? 'border-[var(--ink-navy)] bg-[var(--paper-warm)]' : 'border-[var(--divider)]'}`} data-testid="convert-mode-full">
                  <RadioGroupItem value="full" id="conv-full" className="mt-1" />
                  <div>
                    <div className="text-sm font-medium text-[var(--ink-navy)]">Full conversion</div>
                    <div className="text-[11px] text-slate-500">Convert ALL in-hand + goldsmith-held material to the target packet.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${mode === 'partial' ? 'border-[var(--ink-navy)] bg-[var(--paper-warm)]' : 'border-[var(--divider)]'}`} data-testid="convert-mode-partial">
                  <RadioGroupItem value="partial" id="conv-partial" className="mt-1" />
                  <div>
                    <div className="text-sm font-medium text-[var(--ink-navy)]">Partial conversion</div>
                    <div className="text-[11px] text-slate-500">Convert a chosen quantity of in-hand stock; goldsmith-held auto-converted.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Partial: how much in-hand to convert */}
            {mode === 'partial' && (
              <Card className="p-3 bg-[var(--paper-warm)]">
                <div className="text-xs font-medium text-[var(--ink-navy)] mb-2">In-hand quantity to convert</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Carats</Label>
                    <Input type="number" step="0.01" min="0" max={inHand.carats}
                      value={convCt} onChange={(e) => setConvCt(e.target.value)}
                      placeholder={`Max ${inHand.carats}`}
                      data-testid="convert-partial-carats-input" />
                  </div>
                  <div>
                    <Label>Pieces</Label>
                    <Input type="number" step="1" min="0" max={inHand.pieces}
                      value={convPcs} onChange={(e) => setConvPcs(e.target.value)}
                      placeholder={`Max ${inHand.pieces}`}
                      data-testid="convert-partial-pieces-input" />
                  </div>
                </div>
                {(remainCt > 0 || remainPcs > 0) && (
                  <div className="mt-3">
                    <Label>What happens to the remainder?</Label>
                    <div className="text-[11px] text-slate-500 mb-1.5">
                      Remaining in-hand after conversion: {formatCarats(remainCt)} ct · {remainPcs} pcs · {formatINR(remainValue)}
                    </div>
                    <RadioGroup value={remainderAction} onValueChange={setRemainderAction} className="grid grid-cols-2 gap-2 mt-1">
                      <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${remainderAction === 'keep' ? 'border-amber-500 bg-amber-50' : 'border-[var(--divider)]'}`} data-testid="convert-remainder-keep">
                        <RadioGroupItem value="keep" id="rem-keep" className="mt-1" />
                        <div>
                          <div className="text-sm">Keep on approval</div>
                          <div className="text-[11px] text-slate-500">Remaining stock stays in the source packet on approval.</div>
                        </div>
                      </label>
                      <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${remainderAction === 'return' ? 'border-rose-500 bg-rose-50' : 'border-[var(--divider)]'}`} data-testid="convert-remainder-return">
                        <RadioGroupItem value="return" id="rem-return" className="mt-1" />
                        <div>
                          <div className="text-sm">Return to vendor</div>
                          <div className="text-[11px] text-slate-500">Auto-generate a return memo &amp; reduce vendor&apos;s on-approval balance.</div>
                        </div>
                      </label>
                    </RadioGroup>
                  </div>
                )}
              </Card>
            )}

            {/* Target packet */}
            <div>
              <Label>Target Packet (the destination of the converted stock)</Label>
              <RadioGroup value={targetMode} onValueChange={setTargetMode} className="grid grid-cols-2 gap-2 mt-1">
                <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${targetMode === 'new' ? 'border-[var(--ink-navy)] bg-[var(--paper-warm)]' : 'border-[var(--divider)]'}`} data-testid="convert-target-new">
                  <RadioGroupItem value="new" id="tgt-new" className="mt-1" />
                  <div>
                    <div className="text-sm font-medium">Create new packet</div>
                    <div className="text-[11px] text-slate-500">Auto-number a fresh purchased packet of the same classification.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer ${targetMode === 'existing' ? 'border-[var(--ink-navy)] bg-[var(--paper-warm)]' : 'border-[var(--divider)]'} ${targets.length === 0 ? 'opacity-60' : ''}`} data-testid="convert-target-existing">
                  <RadioGroupItem value="existing" id="tgt-existing" className="mt-1" disabled={targets.length === 0} />
                  <div>
                    <div className="text-sm font-medium">Add to existing packet</div>
                    <div className="text-[11px] text-slate-500">{targets.length === 0 ? 'No eligible packets (same classification + vendor)' : `${targets.length} eligible packet(s) available`}</div>
                  </div>
                </label>
              </RadioGroup>

              {targetMode === 'new' && (
                <div className="mt-2">
                  <Label>Description (optional)</Label>
                  <Input value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="e.g. Customer X order — converted from D-RB-002"
                    data-testid="convert-new-desc" />
                </div>
              )}

              {targetMode === 'existing' && targets.length > 0 && (
                <div className="mt-2">
                  <Label>Existing packet</Label>
                  <SearchableSelect
                    items={targets}
                    value={targetId}
                    onValueChange={(v) => setTargetId(v || '')}
                    itemToLabel={(t) => `${t.packet_no} · ${formatCarats(t.balance_carats)} ct · ${t.balance_pieces} pcs · ${formatINR(t.balance_value)}`}
                    searchKeys={['packet_no', 'classification_shape', 'classification_schema']}
                    placeholder="Select existing packet…"
                    searchPlaceholder="Search packet no…"
                    testId="convert-existing-select"
                  />
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Settlement reference, terms, etc." data-testid="convert-note" />
            </div>

            {/* Wave 3.36 — Credit days for the new payable */}
            <div>
              <Label>Credit Days <span className="text-slate-400 text-[10px]">(optional)</span></Label>
              <Input
                type="number" min={0} max={365}
                placeholder="e.g. 30 — leave blank for none"
                value={creditDays}
                onChange={(e) => setCreditDays(e.target.value.replace(/[^0-9]/g, ''))}
                data-testid="convert-credit-days-input"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">
                {creditDays && Number(creditDays) > 0 ? (
                  <>Due date will be ≈ <strong>
                    {(() => {
                      const d = new Date();
                      d.setDate(d.getDate() + Number(creditDays));
                      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    })()}
                  </strong>. Payment reminders fire at T-7, T-1, T0.</>
                ) : (
                  <span className="text-slate-400">Blank → no due date / no reminders for this purchase.</span>
                )}
              </div>
            </div>

            {/* Review */}
            <Card className="p-3 bg-emerald-50/40 border-emerald-200">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">Review</div>
              <div className="text-sm text-emerald-900 flex items-center flex-wrap gap-1">
                <Badge className="bg-white text-emerald-700 border border-emerald-300">{mode === 'full' ? 'Full' : 'Partial'}</Badge>
                <span className="font-mono">{formatCarats(willConvertCt)} ct · {willConvertPcs} pcs · {formatINR(willConvertValue)}</span>
                <ArrowRight size={12} className="mx-1" />
                <span className="font-medium">
                  {targetMode === 'new' ? 'New purchased packet' :
                    (targets.find(t => t.id === targetId)?.packet_no || 'Select existing packet')}
                </span>
              </div>
              {mode === 'partial' && (remainCt > 0 || remainPcs > 0) && (
                <div className="text-[11px] text-emerald-800 mt-1">
                  Remainder: {formatCarats(remainCt)} ct · {remainPcs} pcs · {formatINR(remainValue)} → {remainderAction === 'return' ? 'returned to vendor (auto memo)' : 'stays on approval'}
                </div>
              )}
              {gsHeld.carats > 0 && (
                <div className="text-[11px] text-emerald-800 mt-1">
                  Goldsmith-held material ({formatCarats(gsHeld.carats)} ct · {gsHeld.pieces} pcs) re-attributed to target packet.
                </div>
              )}
              {error && <div className="text-[11px] text-rose-600 mt-2">⚠ {error}</div>}
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy || loading || !!error}
            data-testid="convert-submit-button"
          >
            {busy ? 'Converting…' : 'Convert to Stock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConvertToStockDialog;
